import React from 'react';
import cn from 'clsx';
import { Point } from 'highcharts';
import i18next from 'i18next';
import ReactDOMServer from 'react-dom/server';
import {
    ColorDirectionThreshold,
    Colours,
    ScoreColor,
    ScoreDirection,
    SiteTrend,
} from '@components-clean/central-monitoring/panels/panel-constants';
import { bankersRound, FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { RAGLabel } from '@components-clean/core/charts/shared/utils';
import { parseDateField } from '@components-clean/core/grid-fe/utils';
import {
    EventRatesData,
    FunnelData,
    FunnelPoints,
    FunnelTrendData,
    FunnelTrendPoint,
    LineThresholdSeries,
    ProportionsOTPoint,
    RateGroupPoint,
    RateGroupTrendData,
    ScatterPlotPoint,
    ScatterPlotSeries,
    TRIOpPoint,
    TRIOpTrendData,
} from '@interfaces-clean/panels.interface';
import {
    ControlHubModelPeriodType,
    EventRatesOTBenchmarkType,
    EventRatesOTCountryBenchmarkType,
    EventRatesOTThresholdType,
    ModelPlotBy,
    ModelPlotCalculation,
    PanelAggregate,
    PanelBenchmark,
    PanelIntervalType,
    PanelSiteScoring,
    PanelThresholdType,
    ProportionsOTBenchmarkType,
    ProportionsOTCountryBenchmarkType,
    ProportionsOTThresholdType,
    ThresholdCalculation,
} from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

const RAG = [ScoreColor.Red, ScoreColor.Amber, ScoreColor.Green];
const RAGGrey = [ScoreColor.Red, ScoreColor.Amber, ScoreColor.Green, ScoreColor.Grey];

export type ScatterThresholdData = {
    value: number;
    type?: PanelThresholdType | null;
};

export const getScoreColor = (scoreColor: ScoreColor, isFaded?: boolean) => {
    switch (scoreColor) {
        case ScoreColor.Red:
            return styles[`rag-colour-red${isFaded ? `-${20}` : ''}`];
        case ScoreColor.Amber:
            return styles[`rag-colour-amber${isFaded ? `-${20}` : ''}`];
        case ScoreColor.Green:
            return styles[`rag-colour-green${isFaded ? `-${20}` : ''}`];
        case ScoreColor.Purple:
            return styles[`rag-colour-purple${isFaded ? `-${20}` : ''}`];
        case ScoreColor.Grey:
            return styles[`rag-colour-grey${isFaded ? `-${25}` : ''}`];
        default:
            break;
    }
};

const getDirection = (direction?: ScoreDirection | null) => {
    switch (direction) {
        case ScoreDirection.Upper:
            return i18next.t('cm:componentsClean.charts.upper');
        case ScoreDirection.Lower:
            return i18next.t('cm:componentsClean.charts.lower');
        case ScoreDirection.Matching:
            return i18next.t('cm:componentsClean.charts.matching');
        default:
            break;
    }
};

const trendToMarker = new Map<SiteTrend, string>([
    [SiteTrend.NoMovement, 'square'],
    [SiteTrend.NoTrend, 'diamond'],
    [SiteTrend.Falling, 'triangle-down'],
    [SiteTrend.Rising, 'triangle'],
    [SiteTrend.Stable, 'circle'],
    [SiteTrend.NotEnoughData, 'square'],
]);

const trendToSeriesName = () =>
    new Map<SiteTrend, string>([
        [SiteTrend.NoMovement, 'No Movement'],
        [SiteTrend.NoTrend, 'No Trend'],
        [SiteTrend.Falling, 'Falling'],
        [SiteTrend.Rising, 'Rising'],
    ]);

export const scoreColorToSeverity = (ragp: ScoreColor, siteScoring?: PanelSiteScoring | null) => {
    switch (ragp) {
        case ScoreColor.Red:
            return i18next.t('cm:componentsClean.charts.ragSeverity.severe');
        case ScoreColor.Amber:
            return i18next.t('cm:componentsClean.charts.ragSeverity.moderate');
        case ScoreColor.Green:
            return i18next.t('cm:componentsClean.charts.ragSeverity.normal');
        case ScoreColor.Purple:
            if (siteScoring === PanelSiteScoring.HIGHER_PURPLE)
                return i18next.t('cm:componentsClean.charts.ragSeverity.higherThanStudyAverage');
            if (siteScoring === PanelSiteScoring.LOWER_PURPLE)
                return i18next.t('cm:componentsClean.charts.ragSeverity.lowerThanStudyAverage');
            break;
        case ScoreColor.Grey:
            return i18next.t('cm:componentsClean.charts.belowMinData');
        default:
            break;
    }
};

const highlightedMarker = (color: ScoreColor, isHighlighted?: boolean | null) => {
    return {
        fillColor: isHighlighted === false ? getScoreColor(color, true) : undefined, // Ignore undefined
    };
};

const mapTriOpPointsToColouredTrend = (points: TRIOpPoint[], trends: Map<SiteTrend, string>, colours: ScoreColor[]) => {
    const array: ScatterPlotSeries[] = [];

    trends.forEach((name, trend) => {
        for (let j = 0; j < colours.length; j++) {
            const color = colours[j];
            const seriesColour = getScoreColor(color);
            array.push({
                name,
                color: seriesColour,
                marker: trendToMarker.get(trend) ?? 'circle',
                data: points
                    .filter((plot) => plot.trend === trend && plot.color === colours[j])
                    .map((p) => ({
                        x: p.x,
                        y: p.y,
                        siteId: p.siteId,
                        siteName: p.siteName,
                        score: p.score,
                        rag: color,
                        marker: highlightedMarker(color, p.isHighlighted),
                    })),
            });
        }
    });

    return array;
};

export const mapTRIOpToScatter = (
    points: TRIOpPoint[],
    stableValue?: number | null,
    stableVariableType?: string | null,
) => {
    const trends = trendToSeriesName();
    trends.set(SiteTrend.Stable, `Stable ${stableValue} ${stableVariableType}`);

    return mapTriOpPointsToColouredTrend(points, trends, RAG);
};

export const mapTRIOpDateToScatter = (
    points: TRIOpPoint[],
    stableValue?: number | null,
    stableVariableType?: string | null,
) => {
    const trends = trendToSeriesName();
    trends.set(SiteTrend.Stable, `Stable ${stableValue} ${stableVariableType}`);
    trends.set(SiteTrend.NotEnoughData, 'Below Min Data');
    trends.delete(SiteTrend.NoMovement);

    return mapTriOpPointsToColouredTrend(points, trends, RAGGrey);
};

export const mapTRIOpTrendToScatter = (data: TRIOpTrendData, panelBenchmark?: PanelAggregate | null) => {
    const array: ScatterPlotSeries[] = [];

    array.push({
        name: panelBenchmark === PanelAggregate.MEAN ? i18next.t('admin:STUDY_MEAN') : i18next.t('admin:STUDY_MEDIAN'),
        color: Colours.LIGHT_BLUE,
        marker: 'circle',
        lineWidth: 1,
        data:
            data.triTrendSitesAverage?.map((p) => ({
                x: p.x,
                y: p.y,
                rag: ScoreColor.None,
            })) ?? [],
    });

    if (data.triTrendSites && Object.keys(data.triTrendSites)?.length) {
        const siteData = data.triTrendSites[Object.keys(data.triTrendSites)[0]];
        const hasData = !!siteData.length;
        if (hasData) {
            array.push({
                name: `Site: ${siteData[0].siteName}`,
                color: Colours.DARK_BLUE,
                marker: 'circle',
                lineWidth: 1,
                data:
                    siteData?.map((p) => ({
                        x: p.x,
                        y: p.y,
                        rag: ScoreColor.None,
                        siteName: p.siteName,
                    })) ?? [],
            });
        }
    }

    return array;
};

export const mapRateGroupToScatter = (points: RateGroupPoint[], siteScoring?: PanelSiteScoring | null) => {
    const ragp = [ScoreColor.Red, ScoreColor.Amber, ScoreColor.Green];
    if (siteScoring === PanelSiteScoring.HIGHER_PURPLE || siteScoring === PanelSiteScoring.LOWER_PURPLE)
        ragp.push(ScoreColor.Purple);

    const array: ScatterPlotSeries[] = [];
    for (let i = 0; i < ragp.length; i++) {
        const color = ragp[i];
        array.push({
            name: scoreColorToSeverity(color, siteScoring) ?? '',
            color: getScoreColor(color),
            marker: 'circle',
            data: points
                .filter((plot) => plot.color === color)
                .map((p) => ({
                    x: p.x,
                    y: p.y,
                    siteId: p.siteId,
                    siteName: p.siteName,
                    score: p.score,
                    rag: color,
                    pValue: p.pValue,
                    direction: p.direction,
                    marker: highlightedMarker(color, p.isHighlighted),
                })),
        });
    }

    return array;
};

export const mapRateGroupTrendToScatter = (data: RateGroupTrendData, studyNumber?: string | null) => {
    const array: ScatterPlotSeries[] = [];

    if (data.rateGroupTrendStudyData) {
        array.push({
            name: studyNumber ?? '',
            color: Colours.LIGHT_BLUE,
            marker: 'circle',
            lineWidth: 1,
            data: data.rateGroupTrendStudyData.map((p) => ({
                x: parseDateField(p.x)?.getTime(),
                y: p.y,
                rag: ScoreColor.None,
            })),
        });

        if (data.rateGroupTrendLineStudy) {
            array.push({
                name: `${studyNumber} Trend`,
                marker: 'circle',
                color: Colours.GOLD,
                lineWidth: 1,
                lineType: 'spline',
                data: data.rateGroupTrendLineStudy.map((p) => ({
                    x: parseDateField(p.x)?.getTime(),
                    y: p.y,
                    rag: ScoreColor.None,
                })),
            });

            array.push({
                name: '',
                marker: 'none',
                color: Colours.GOLD,
                lineWidth: 0,
                lineType: 'areasplinerange',
                data: data.rateGroupTrendLineStudy.map<[number, number, number]>((p) => {
                    return [
                        parseDateField(p.x)?.getTime() ?? 0,
                        data.rateGroupTrendLowerThresholdStudy?.filter((lwr) => lwr.x === p.x)[0]?.y ?? 0,
                        data.rateGroupTrendUpperThresholdStudy?.filter((lwr) => lwr.x === p.x)[0]?.y ?? 0,
                    ];
                }),
            });
        }
    }

    if (data.rateGroupTrendSites && Object.keys(data.rateGroupTrendSites)?.length) {
        const siteData = data.rateGroupTrendSites[Object.keys(data.rateGroupTrendSites)[0]];
        if (siteData && siteData.data?.length) {
            array.push({
                name: siteData.siteName ?? '',
                color: Colours.DARK_BLUE,
                marker: 'circle',
                lineWidth: 1,
                data: siteData.data.map((p) => ({
                    x: parseDateField(p.x)?.getTime(),
                    y: p.y,
                    rag: ScoreColor.None,
                })),
            });

            if (siteData.line) {
                array.push({
                    name: `${siteData.siteName} Trend`,
                    marker: 'circle',
                    color: Colours.PURPLE,
                    lineWidth: 1,
                    lineType: 'spline',
                    data: siteData.line.map((p) => ({
                        x: parseDateField(p.x)?.getTime(),
                        y: p.y,
                        rag: ScoreColor.None,
                    })),
                });

                array.push({
                    name: '',
                    marker: 'none',
                    color: Colours.PURPLE,
                    lineWidth: 0,
                    lineType: 'areasplinerange',
                    data: siteData.line.map<[number, number, number]>((p) => {
                        return [
                            parseDateField(p.x)?.getTime() ?? 0,
                            siteData.lowerThreshold?.filter((lwr) => lwr.x === p.x)[0]?.y ?? 0,
                            siteData.upperThreshold?.filter((lwr) => lwr.x === p.x)[0]?.y ?? 0,
                        ];
                    }),
                });
            }
        }
    }

    return array;
};

export const mapFunnelToScatter = (points: FunnelPoints[]) => {
    const array: ScatterPlotSeries[] = [];
    for (let i = 0; i < RAG.length; i++) {
        const color = RAG[i];
        array.push({
            name: scoreColorToSeverity(color) ?? '',
            color: getScoreColor(color),
            marker: 'circle',
            data: points
                .filter((plot) => plot.color === color)
                .map((p) => ({
                    x: p.x,
                    y: p.y,
                    siteId: p.siteId,
                    siteName: p.siteName,
                    score: p.score,
                    rag: color,
                    direction: p.direction,
                    marker: highlightedMarker(color, p.isHighlighted),
                })),
        });
    }

    return array;
};

export const mapFunnelTrendToScatter = (
    data: FunnelTrendData,
    confidenceInterval?: PanelIntervalType | null,
    studyNumber?: string | null,
) => {
    const thresholds: LineThresholdSeries[] = [];

    const isStudy = data.triTrendStudy?.length;
    const siteId = isStudy ? undefined : Object.keys(data.triTrendSites!)[0];

    const points = data.triTrendStudy?.length
        ? data.triTrendStudy
        : data.triTrendSites && Object.keys(data.triTrendSites)?.length
          ? data.triTrendSites[Object.keys(data.triTrendSites)[0]]
          : [];
    const seriesName = (isStudy ? studyNumber : data.siteNames![siteId!]) ?? '';

    const trendLine: ScatterPlotSeries = {
        name: seriesName,
        color: styles['trend-blue'],
        marker: 'circle',
        data: points.map((p) => ({
            x: p.x,
            y: p.y,
            siteId: p.siteId,
            siteName: seriesName,
            score: p.score,
            direction: p.direction,
            rag: ScoreColor.None,
            marker: highlightedMarker(ScoreColor.None, p.isHighlighted),
        })),
        lineWidth: 1,
    };

    const choosePoints = (
        studyPoints: FunnelTrendPoint[] | null | undefined,
        sitePoints: FunnelTrendPoint[] | null | undefined,
    ) => {
        if (studyPoints?.length) return studyPoints;
        if (sitePoints?.length) return sitePoints;
        return [];
    };

    const lineType = confidenceInterval === PanelIntervalType.APPROXIMATE ? ('spline' as const) : ('line' as const);
    const lowerAmberLimit = choosePoints(data.studyLowerLimitAmberPoints, data.lowerLimitAmberPoints);
    const lowerRedLimit = choosePoints(data.studyLowerLimitRedPoints, data.lowerLimitRedPoints);
    const upperAmberLimit = choosePoints(data.studyUpperLimitAmberPoints, data.upperLimitAmberPoints);
    const upperRedLimit = choosePoints(data.studyUpperLimitRedPoints, data.upperLimitRedPoints);

    if (lowerAmberLimit?.length)
        thresholds.push({
            type: lineType,
            color: getScoreColor(ScoreColor.Amber) ?? '',
            style: 'Dash',
            data: lowerAmberLimit,
        });

    if (lowerRedLimit?.length)
        thresholds.push({
            type: lineType,
            color: getScoreColor(ScoreColor.Red) ?? '',
            style: 'Dash',
            data: lowerRedLimit,
        });

    if (upperAmberLimit?.length)
        thresholds.push({
            type: lineType,
            color: getScoreColor(ScoreColor.Amber) ?? '',
            style: 'Dash',
            data: upperAmberLimit,
        });

    if (upperRedLimit?.length)
        thresholds.push({
            type: lineType,
            color: getScoreColor(ScoreColor.Red) ?? '',
            style: 'Dash',
            data: upperRedLimit,
        });

    if (confidenceInterval === PanelIntervalType.EXACT && data.benchmarkLine)
        thresholds.push({
            type: lineType,
            color: 'black',
            style: 'Dash',
            data: data.benchmarkLine,
        });

    if (confidenceInterval === PanelIntervalType.EXACT) {
        const maxX = Math.max(...trendLine.data.map((p) => Number((p as ScatterPlotPoint).x)));
        const benchmarkLine = data.benchmarkLine?.length ? (data.benchmarkLine ?? []) : (data.studyBenchmarkLine ?? []);
        const benchmarkPlot: LineThresholdSeries = {
            data:
                benchmarkLine
                    ?.filter((p) => Number(!p.x || isNaN(p.x) ? 0 : p.x) <= maxX)
                    ?.map((p) => ({
                        x: !p.x || isNaN(p.x) ? 0 : p.x,
                        y: !p.y || isNaN(p.y) ? 0 : p.y,
                    })) ?? [],
            style: 'Dash',
            type: 'line',
            color: 'black',
        };
        thresholds.push(benchmarkPlot);
    }

    return { trendLine, thresholds, seriesName };
};

const getThresholdLabel = (type?: PanelThresholdType | null) => {
    switch (type) {
        case PanelThresholdType.SAMPLE_MEAN:
            return i18next.t('admin:SAMPLE_MEAN');
        case PanelThresholdType.SAMPLE_MEDIAN:
            return i18next.t('admin:SAMPLE_MEDIAN');
        case PanelThresholdType.STUDY_MEAN:
            return i18next.t('admin:STUDY_MEAN');
        case PanelThresholdType.STUDY_MEDIAN:
            return i18next.t('admin:STUDY_MEDIAN');
        case PanelThresholdType.STUDY_SPECIFIC:
            return i18next.t('admin:STUDY_SPECIFIC');
        default:
            break;
    }
};

const getThresholdLabelFromCalculation = (type?: ThresholdCalculation | null) => {
    switch (type) {
        case ThresholdCalculation.MEAN_SD:
            return i18next.t('cm:componentsClean.charts.meanSD');
        case ThresholdCalculation.POISSON:
            return i18next.t('cm:componentsClean.charts.poisson');
        case ThresholdCalculation.STUDY_SPECIFIC:
            return i18next.t('admin:STUDY_SPECIFIC');
        default:
            break;
    }
};

export const getTRIOpSubtitle = (
    threshold1?: ScatterThresholdData,
    threshold2?: ScatterThresholdData,
    showSeverity?: boolean,
) => {
    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            {showSeverity && (
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ color: styles['rag-colour-red'], fontSize: '11px', marginRight: '3px' }}>✺</span>
                        {scoreColorToSeverity(ScoreColor.Red)}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ color: styles['rag-colour-amber'], fontSize: '11px', marginRight: '3px' }}>
                            ✺
                        </span>
                        {scoreColorToSeverity(ScoreColor.Amber)}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ color: styles['rag-colour-green'], fontSize: '11px', marginRight: '3px' }}>
                            ✺
                        </span>
                        {scoreColorToSeverity(ScoreColor.Green)}
                    </span>
                </div>
            )}
            <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
                {threshold1 != null && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                            style={{
                                color: Colours.AMBER,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabel(threshold1.type)}: {FormatNumberForChart(threshold1.value)}
                    </div>
                )}
                {threshold2 != null && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                            style={{
                                color: Colours.RED,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabel(threshold2.type)}: {FormatNumberForChart(threshold2.value)}
                    </div>
                )}
            </div>
        </div>,
    );
};
export const getTRIOpSubtitleComponents = (
    threshold1?: ScatterThresholdData,
    threshold2?: ScatterThresholdData,
    showSeverity?: boolean,
) => {
    return (
        <div style={{ paddingLeft: 5 }}>
            {showSeverity && (
                <div className="k-justify-content-left k-pb-2">
                    <div>
                        <span style={{ color: styles['rag-colour-red'], fontSize: '15px', marginRight: '12px' }}>
                            ✺
                        </span>
                        {scoreColorToSeverity(ScoreColor.Red)}
                    </div>
                    <div>
                        <span style={{ color: styles['rag-colour-amber'], fontSize: '15px', marginRight: '12px' }}>
                            ✺
                        </span>
                        {scoreColorToSeverity(ScoreColor.Amber)}
                    </div>
                    <div>
                        <span style={{ color: styles['rag-colour-green'], fontSize: '15px', marginRight: '12px' }}>
                            ✺
                        </span>
                        {scoreColorToSeverity(ScoreColor.Green)}
                    </div>
                </div>
            )}
            <div className="k-gap-sm">
                {threshold1 != null && (
                    <div className="k-justify-items-left">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {getThresholdLabel(threshold1.type)}:
                        </span>
                        <div>
                            <span
                                style={{
                                    color: Colours.AMBER,
                                    fontSize: 'large',
                                    marginRight: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                - - -
                            </span>
                            {FormatNumberForChart(threshold1.value)}
                        </div>
                    </div>
                )}
                {threshold2 != null && (
                    <div className="k-justify-items-left">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {getThresholdLabel(threshold2.type)}:
                        </span>
                        <div>
                            <span
                                style={{
                                    color: Colours.RED,
                                    fontSize: 'large',
                                    marginRight: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                - - -
                            </span>
                            {FormatNumberForChart(threshold2.value)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const getFunnelSubtitle = (
    benchmarkType?: PanelBenchmark | null,
    benchmarkValue?: number | null,
    hideLower?: boolean | null,
    hideUpper?: boolean | null,
    lowerAmber?: number | null,
    upperAmber?: number | null,
    lowerRed?: number | null,
    upperRed?: number | null,
) => {
    let benchmarkLabel = '';
    switch (benchmarkType) {
        case PanelBenchmark.STUDY_MEAN:
            benchmarkLabel = i18next.t('admin:STUDY_SITE_MEAN');
            break;
        case PanelBenchmark.STUDY_MEDIAN:
            benchmarkLabel = i18next.t('admin:STUDY_SITE_MEDIAN');
            break;
        case PanelBenchmark.STUDY_SPECIFIC:
            benchmarkLabel = i18next.t('admin:STUDY_SPECIFIC');
            break;
        default:
            break;
    }
    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span
                    style={{
                        color: 'black',
                        fontSize: 'large',
                        marginRight: '5px',
                    }}
                >
                    - - -
                </span>
                {benchmarkLabel}: {FormatNumberForChart(benchmarkValue)}
            </div>
            {!hideUpper && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {i18next.t('cm:componentsClean.charts.upperThresholds')}:
                    <span
                        style={{
                            color: Colours.RED,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {upperRed}
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {upperAmber}
                </div>
            )}
            {!hideLower && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {i18next.t('cm:componentsClean.charts.lowerThresholds')}:
                    <span
                        style={{
                            color: Colours.RED,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {lowerRed}
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {lowerAmber}
                </div>
            )}
        </div>,
    );
};

export const getFunnelSubtitleComponents = (
    benchmarkType?: PanelBenchmark | null,
    benchmarkValue?: number | null,
    hideLower?: boolean | null,
    hideUpper?: boolean | null,
    lowerAmber?: number | null,
    upperAmber?: number | null,
    lowerRed?: number | null,
    upperRed?: number | null,
    isTrend?: boolean | null,
    isSiteBenchmark?: boolean | null,
) => {
    let benchmarkLabel = '';
    switch (benchmarkType) {
        case PanelBenchmark.STUDY_MEAN:
            if (isTrend)
                benchmarkLabel = i18next.t(isSiteBenchmark ? 'admin:STUDY_SITE_MEAN' : 'admin:MEAN_CUMULATIVE_RATE');
            else benchmarkLabel = i18next.t('admin:STUDY_SITE_MEAN');
            break;
        case PanelBenchmark.STUDY_MEDIAN:
            if (isTrend)
                benchmarkLabel = i18next.t(
                    isSiteBenchmark ? 'admin:STUDY_SITE_MEDIAN' : 'admin:MEDIAN_CUMULATIVE_RATE',
                );
            else benchmarkLabel = i18next.t('admin:STUDY_SITE_MEDIAN');
            break;
        case PanelBenchmark.STUDY_SPECIFIC:
            benchmarkLabel = i18next.t('admin:STUDY_SPECIFIC');
            break;
        default:
            break;
    }
    return (
        <div style={{ paddingLeft: 5 }}>
            <div className="k-justify-items-left k-pb-2">
                <div className="k-white-space-nowrap">
                    <span
                        style={{
                            color: 'black',
                            fontSize: 'large',
                            marginRight: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        - - -
                    </span>
                    {benchmarkLabel}: {FormatNumberForChart(benchmarkValue)}
                </div>
            </div>
            {!hideUpper && (
                <div className="k-justify-items-left">
                    <span
                        style={{
                            fontWeight: '500',
                        }}
                    >
                        {i18next.t('cm:componentsClean.charts.upperThresholds')}:
                    </span>
                    <div>
                        <span
                            style={{
                                color: Colours.RED,
                                fontSize: 'large',
                                marginRight: '12px',
                                fontWeight: 'bold',
                            }}
                        >
                            - - -
                        </span>
                        {upperRed}
                    </div>
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginRight: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        - - -
                    </span>
                    {upperAmber}
                </div>
            )}
            {!hideLower && (
                <div className="k-justify-items-left">
                    <span
                        style={{
                            fontWeight: '500',
                        }}
                    >
                        {i18next.t('cm:componentsClean.charts.lowerThresholds')}:
                    </span>
                    <div>
                        <span
                            style={{
                                color: Colours.RED,
                                fontSize: 'large',
                                marginRight: '12px',
                                fontWeight: 'bold',
                            }}
                        >
                            - - -
                        </span>
                        {lowerRed}
                    </div>
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginRight: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        - - -
                    </span>
                    {lowerAmber}
                </div>
            )}
        </div>
    );
};
export const triOpTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;

    let title = scoreColorToSeverity(plotPoint.rag);
    if (plotPoint.rag === ScoreColor.Grey) title = point.series.name;
    else if (plotPoint.rag !== ScoreColor.None) title += `: ${point.series.name}`;

    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{title}</b>
            <br />
            {i18next.t('Site')}: {plotPoint.siteName}
            {plotPoint.rag !== undefined && plotPoint.rag !== ScoreColor.None && plotPoint.rag !== ScoreColor.Grey && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.rag')}: {RAGLabel(plotPoint.rag)}
                </>
            )}
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}: {FormatNumberForChart(plotPoint.x)}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
            {plotPoint.score !== undefined && plotPoint.rag !== ScoreColor.Grey && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.score')}: {FormatNumberForChart(plotPoint.score)}
                </>
            )}
            {plotPoint.pValue !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.pValue')}: {FormatNumberForChart(plotPoint.pValue)}
                </>
            )}
        </div>,
    );
};

export const getTriOpTrendSubtitle = (threshold1?: ScatterThresholdData, threshold2?: ScatterThresholdData) => {
    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
            {threshold1 != null && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginRight: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {getThresholdLabel(threshold1.type)}: {FormatNumberForChart(threshold1.value)}
                </div>
            )}
            {threshold2 != null && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                        style={{
                            color: Colours.RED,
                            fontSize: 'large',
                            marginRight: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {getThresholdLabel(threshold2.type)}: {FormatNumberForChart(threshold2.value)}
                </div>
            )}
        </div>,
    );
};

export const getTRIOpTrendSubtitleComponents = (
    threshold1?: ScatterThresholdData,
    threshold2?: ScatterThresholdData,
) => {
    return (
        <div className="k-text-center">
            <div className="k-gap-sm">
                {threshold1 != null && (
                    <div className="k-d-flex k-align-items-center">
                        <span
                            style={{
                                color: Colours.AMBER,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabel(threshold1.type)}: {FormatNumberForChart(threshold1.value)}
                    </div>
                )}
                {threshold2 != null && (
                    <div className="k-d-flex k-align-items-center">
                        <span
                            style={{
                                color: Colours.RED,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabel(threshold2.type)}: {FormatNumberForChart(threshold2.value)}
                    </div>
                )}
            </div>
        </div>
    );
};

export const triOpTrendTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;
    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{point.series.name}</b>
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}: {FormatNumberForChart(plotPoint.x)}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
            {}
        </div>,
    );
};

export const rateGroupTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;

    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{point.series.name}</b>
            {plotPoint.siteName != null && (
                <>
                    <br />
                    {i18next.t('Site')}: {plotPoint.siteName}
                </>
            )}
            {plotPoint.rag !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.rag')}: {getDirection(plotPoint.direction)}{' '}
                    {RAGLabel(plotPoint.rag)}
                </>
            )}
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}: {FormatNumberForChart(plotPoint.x)}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
            {plotPoint.score !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.score')}: {FormatNumberForChart(plotPoint.score)}
                </>
            )}
            {plotPoint.pValue !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.pValue')}: {FormatNumberForChart(plotPoint.pValue)}
                </>
            )}
        </div>,
    );
};

export const rateGroupTrendTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;

    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{point.series.name}</b>
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}:{' '}
            {stringDateWithoutTimeFromUtc(new Date(point.x), 'ddd MMM D YYYY')}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
            <br />
        </div>,
    );
};

export const funnelTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;

    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{point.series.name}</b>
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}: {FormatNumberForChart(plotPoint.x)}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
            {plotPoint.siteName !== undefined && (
                <>
                    <br />
                    {i18next.t('Site')}: {plotPoint.siteName}
                </>
            )}
            {plotPoint.direction !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.rag')}: {getDirection(plotPoint.direction)}{' '}
                    {RAGLabel(plotPoint.rag)}
                </>
            )}
            {plotPoint.score !== undefined && (
                <>
                    <br />
                    {i18next.t('cm:componentsClean.charts.score')}: {FormatNumberForChart(plotPoint.score)}
                </>
            )}
        </div>,
    );
};

export const funnelThresholdSeries = (
    data: FunnelData,
    confidenceInterval?: PanelIntervalType | null,
    hideLower?: boolean | null,
    hideUpper?: boolean | null,
) => {
    const array: LineThresholdSeries[] = [];

    const lineType = confidenceInterval === PanelIntervalType.APPROXIMATE ? ('spline' as const) : ('line' as const);

    if (!hideLower) {
        if (data.lowerLimitAmberPoints)
            array.push({
                type: lineType,
                color: getScoreColor(ScoreColor.Amber) ?? '',
                style: 'Dash',
                data: data.lowerLimitAmberPoints,
            });

        if (data.lowerLimitRedPoints)
            array.push({
                type: lineType,
                color: getScoreColor(ScoreColor.Red) ?? '',
                style: 'Dash',
                data: data.lowerLimitRedPoints,
            });
    }

    if (!hideUpper) {
        if (data.upperLimitAmberPoints)
            array.push({
                type: lineType,
                color: getScoreColor(ScoreColor.Amber) ?? '',
                style: 'Dash',
                data: data.upperLimitAmberPoints,
            });

        if (data.upperLimitRedPoints)
            array.push({
                type: lineType,
                color: getScoreColor(ScoreColor.Red) ?? '',
                style: 'Dash',
                data: data.upperLimitRedPoints,
            });
    }

    if (confidenceInterval === PanelIntervalType.EXACT && data.benchmarkLine)
        array.push({
            type: lineType,
            color: 'black',
            style: 'Dash',
            data: data.benchmarkLine,
        });

    return array;
};

export const funnelTrendTooltip = (point: Point) => {
    const plotPoint = point as unknown as ScatterPlotPoint;

    return ReactDOMServer.renderToStaticMarkup(
        <div>
            <b>{point.series.name}</b>
            <br />
            {i18next.t('cm:componentsClean.charts.xAxis')}: {FormatNumberForChart(plotPoint.x)}
            <br />
            {i18next.t('cm:componentsClean.charts.yAxis')}: {FormatNumberForChart(plotPoint.y)}
        </div>,
    );
};

export const getFunnelTrendSubtitle = (
    benchmarkType?: PanelBenchmark | null,
    benchmarkValue?: number | null,
    hideLower?: boolean | null,
    hideUpper?: boolean | null,
    lowerAmber?: number | null,
    upperAmber?: number | null,
    lowerRed?: number | null,
    upperRed?: number | null,
    isSiteBenchmark?: boolean | null,
) => {
    let benchmarkLabel = '';
    switch (benchmarkType) {
        case PanelBenchmark.STUDY_MEAN:
            benchmarkLabel = i18next.t(isSiteBenchmark ? 'admin:STUDY_SITE_MEAN' : 'admin:MEAN_CUMULATIVE_RATE');
            break;
        case PanelBenchmark.STUDY_MEDIAN:
            benchmarkLabel = i18next.t(isSiteBenchmark ? 'admin:STUDY_SITE_MEDIAN' : 'admin:MEDIAN_CUMULATIVE_RATE');
            break;
        case PanelBenchmark.STUDY_SPECIFIC:
            benchmarkLabel = i18next.t('admin:STUDY_SPECIFIC');
            break;
        default:
            break;
    }
    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span
                    style={{
                        color: 'black',
                        fontSize: 'large',
                        marginRight: '5px',
                    }}
                >
                    - - -
                </span>
                {benchmarkLabel}: {FormatNumberForChart(benchmarkValue)}
            </div>
            {!hideUpper && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {i18next.t('cm:componentsClean.charts.upperThresholds')}:
                    <span
                        style={{
                            color: Colours.RED,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {upperRed}
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {upperAmber}
                </div>
            )}
            {!hideLower && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {i18next.t('cm:componentsClean.charts.lowerThresholds')}:
                    <span
                        style={{
                            color: Colours.RED,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {lowerRed}
                    <span
                        style={{
                            color: Colours.AMBER,
                            fontSize: 'large',
                            marginInline: '5px',
                        }}
                    >
                        - - -
                    </span>
                    {lowerAmber}
                </div>
            )}
        </div>,
    );
};

export function getTRIOpDateSubtitle(
    threshold1: number | undefined,
    threshold2: number | undefined,
    thresholdCalculation: ThresholdCalculation | null | undefined,
    modelRate?: number | null,
) {
    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                {modelRate && <div>Sample Rate: {FormatNumberForChart(modelRate)}</div>}
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ color: styles['rag-colour-red'], fontSize: '11px', marginRight: '3px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Red)}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ color: styles['rag-colour-amber'], fontSize: '11px', marginRight: '3px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Amber)}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ color: styles['rag-colour-green'], fontSize: '11px', marginRight: '3px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Green)}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ color: styles['rag-colour-grey'], fontSize: '11px', marginRight: '3px' }}>✺</span>
                    {i18next.t('cm:componentsClean.charts.belowMinRecords')}
                </span>
            </div>

            <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
                {threshold1 != null && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                            style={{
                                color: Colours.AMBER,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabelFromCalculation(thresholdCalculation)}: {FormatNumberForChart(threshold1)}
                    </div>
                )}
                {threshold2 != null && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                            style={{
                                color: Colours.RED,
                                fontSize: 'large',
                                marginRight: '5px',
                            }}
                        >
                            - - -
                        </span>
                        {getThresholdLabelFromCalculation(thresholdCalculation)}: {FormatNumberForChart(threshold2)}
                    </div>
                )}
            </div>
        </div>,
    );
}

export const getTRIOpDateSubtitleComponents = (
    threshold1?: number | null,
    threshold2?: number | null,
    thresholdCalculation?: ThresholdCalculation | null | undefined,
    modelRate?: number | null,
) => {
    return (
        <div style={{ paddingLeft: 5, width: '146px' }}>
            <div className="k-justify-content-left k-pb-2">
                {modelRate && <div>Sample Rate: {FormatNumberForChart(modelRate)}</div>}
                <div>
                    <span style={{ color: styles['rag-colour-red'], fontSize: '15px', marginRight: '12px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Red)}
                </div>
                <div>
                    <span style={{ color: styles['rag-colour-amber'], fontSize: '15px', marginRight: '12px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Amber)}
                </div>
                <div>
                    <span style={{ color: styles['rag-colour-green'], fontSize: '15px', marginRight: '12px' }}>✺</span>
                    {scoreColorToSeverity(ScoreColor.Green)}
                </div>
                <div className="k-d-flex-row">
                    <span
                        style={{
                            color: styles['rag-colour-grey'],
                            fontSize: '15px',
                            marginRight: '12px',
                            display: 'inline-block',
                        }}
                    >
                        ✺
                    </span>
                    <span>{i18next.t('cm:componentsClean.charts.belowMinRecords')}</span>
                </div>
            </div>
            <div className="k-gap-sm">
                {threshold1 != null && (
                    <div className="k-justify-items-left">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {getThresholdLabelFromCalculation(thresholdCalculation)}:
                        </span>
                        <div>
                            <span
                                style={{
                                    color: Colours.AMBER,
                                    fontSize: 'large',
                                    marginRight: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                - - -
                            </span>
                            {FormatNumberForChart(threshold1)}
                        </div>
                    </div>
                )}
                {threshold2 != null && (
                    <div className="k-justify-items-left">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {getThresholdLabelFromCalculation(thresholdCalculation)}:
                        </span>
                        <div>
                            <span
                                style={{
                                    color: Colours.RED,
                                    fontSize: 'large',
                                    marginRight: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                - - -
                            </span>
                            {FormatNumberForChart(threshold2)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const getScatterPlotBounds = (data: ScatterPlotSeries[], threshold1?: number, threshold2?: number) => {
    // No need to work out bounds if there are no thresholds
    if (threshold1 == null && threshold2 == null) return [undefined, undefined];

    let minY: number | undefined;
    let maxY: number | undefined;

    data.forEach((series) => {
        series.data.forEach((point) => {
            if (Array.isArray(point)) {
                if (minY === undefined || point[1] < minY) minY = point[1];
                if (maxY === undefined || point[2] > maxY) maxY = point[2];
            } else {
                if (point.y === undefined) return;

                if (minY === undefined || point.y < minY) minY = point.y;
                if (maxY === undefined || point.y > maxY) maxY = point.y;
            }
        });
    });

    let lowerBounds: number | undefined;
    let upperBounds: number | undefined;
    if (threshold1 != null && minY != null) lowerBounds = Math.min(threshold1, minY);
    if (threshold2 != null && maxY != null) upperBounds = Math.max(threshold2, maxY);

    return [lowerBounds, upperBounds];
};

const convertPlotCalculation = (plotCalculation: ModelPlotCalculation, plotBy: ModelPlotBy) => {
    const plotCalculationText = i18next.t(`cm:componentsClean.charts.plotCalculation.${plotCalculation}`);
    const plotByText = i18next.t(`cm:componentsClean.charts.plotBy.${plotBy}`);
    return `${plotCalculationText} - ${plotByText}`;
};

export const getEventRateSubtitle = (
    eventRateBenchmarkType?: EventRatesOTBenchmarkType | EventRatesOTCountryBenchmarkType | null,
    benchmarkValue?: number | null,
    eventRateThresholdType?: EventRatesOTThresholdType | null,
    plotCalculation?: ModelPlotCalculation | null,
    plotBy?: ModelPlotBy | null,
    upperAmber?: number | null,
    lowerAmber?: number | null,
    upperRed?: number | null,
    lowerRed?: number | null,
    minSubjects?: number | null,
    minTimeOnStudy?: { unit: number | null; period: ControlHubModelPeriodType | null },
) => {
    const eventRateBenchmarkText = i18next.t(`cm:componentsClean.charts.benchmarkTypeValue.${eventRateBenchmarkType}`);
    const thresholdText = i18next.t(`cm:componentsClean.charts.thresholdTypeValue.${eventRateThresholdType}`);

    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            <div>
                <div>
                    {benchmarkValue != null && (
                        <span className="k-justify-items-left k-pb-2">
                            <span>
                                <span style={{ paddingRight: '16px' }}>
                                    <span
                                        style={{
                                            color: Colours.BLACK,
                                            fontSize: 'large',
                                            marginRight: '12px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {eventRateBenchmarkText}: {FormatNumberForChart(benchmarkValue)}
                                </span>
                            </span>
                        </span>
                    )}
                    {eventRateThresholdType != null && (
                        <span className="k-justify-items-left k-pb-2">
                            <span className="k-gap-sm">
                                <span style={{ paddingRight: '16px' }}>
                                    <span
                                        style={{
                                            fontWeight: '500',
                                        }}
                                    >
                                        {i18next.t('cm:componentsClean.charts.thresholdType')}:{' '}
                                    </span>
                                    {thresholdText}
                                </span>
                            </span>
                        </span>
                    )}
                </div>
                <div className="k-justify-items-left k-pb-2">
                    {/* upper thresholds */}
                    {upperRed != null && upperAmber != null && (
                        <span style={{ paddingRight: '8px' }}>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.upperThresholds')}:{' '}
                            </span>
                            <span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.RED,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(upperRed)}
                                </span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.AMBER,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(upperAmber)}
                                </span>
                            </span>
                        </span>
                    )}
                    {/* lower thresholds */}
                    {lowerRed != null && lowerAmber != null && (
                        <span>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.lowerThresholds')}:{' '}
                            </span>
                            <span className="k-gap-sm">
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.RED,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(lowerRed)}
                                </span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.AMBER,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(lowerAmber)}
                                </span>
                            </span>
                        </span>
                    )}
                </div>
                <div style={{ paddingBottom: '8px' }}>
                    {plotCalculation != null && plotBy != null && (
                        <span style={{ paddingRight: '8px' }} className="k-justify-items-left k-pb-2">
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.plotCalculations')}:{' '}
                            </span>
                            {convertPlotCalculation(plotCalculation, plotBy)}
                        </span>
                    )}
                    {minSubjects != null && (
                        <span className="k-justify-items-left k-pb-2">
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.minSubjects')}:{' '}
                            </span>
                            {minSubjects}
                        </span>
                    )}
                    {minTimeOnStudy != null && (
                        <div style={{ paddingBottom: '8px' }} className="k-justify-items-left k-pb-2">
                            <span style={{ fontWeight: '500' }}>
                                {i18next.t('cm:componentsClean.charts.minTimeOnStudy')}{' '}
                            </span>
                            <span>
                                {minTimeOnStudy.unit}{' '}
                                {i18next.t(`cm:componentsClean.charts.minTimePeriod.${minTimeOnStudy.period}`)}
                            </span>
                        </div>
                    )}
                </div>
                {/* min data; no new data; no new data/below min data; data lag zone */}
                <div className="k-justify-items-left">
                    {/* below min data */}
                    <span style={{ paddingRight: '8px' }}>
                        <span
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-flex',
                                borderRadius: '50%',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />{' '}
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </span>
                    {/* no new data */}
                    <span style={{ paddingInline: '8px' }}>
                        <span
                            style={{
                                backgroundColor: Colours.RED,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        <span
                            style={{
                                backgroundColor: Colours.AMBER,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        <span
                            style={{
                                backgroundColor: Colours.GREEN,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />{' '}
                        {i18next.t('cm:componentsClean.charts.noNewData')}
                    </span>
                    {/* no new data & below min data */}
                    <span style={{ paddingInline: '8px' }}>
                        <span
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />{' '}
                        {i18next.t('cm:componentsClean.charts.noNewData')} &{' '}
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </span>
                    {/* data lag zone */}
                    <span style={{ paddingLeft: '8px' }}>
                        <span
                            style={{
                                borderLeft: '1px solid #b3b3b3',
                                borderRight: '1px solid #b3b3b3',
                                backgroundColor: styles['rag-colour-grey-25'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        {i18next.t('cm:componentsClean.charts.dataLagZone')}
                    </span>
                </div>
            </div>
        </div>,
    );
};

export const getEventRateSubtitleComponents = (
    eventRateBenchmarkType?: EventRatesOTBenchmarkType | EventRatesOTCountryBenchmarkType | null,
    benchmarkValue?: number | null,
    eventRateThresholdType?: EventRatesOTThresholdType | null,
    plotCalculation?: ModelPlotCalculation | null,
    plotBy?: ModelPlotBy | null,
    upperAmber?: number | null,
    lowerAmber?: number | null,
    upperRed?: number | null,
    lowerRed?: number | null,
    minSubjects?: number | null,
    minTimeOnStudy?: { unit?: number | null; period?: ControlHubModelPeriodType | null },
) => {
    const eventRateBenchmarkText = i18next.t(`cm:componentsClean.charts.benchmarkTypeValue.${eventRateBenchmarkType}`);
    const thresholdText = i18next.t(`cm:componentsClean.charts.thresholdTypeValue.${eventRateThresholdType}`);
    return (
        <div style={{ paddingLeft: 5, width: '270px' }}>
            <div>
                {benchmarkValue != null &&
                    eventRateBenchmarkType !== EventRatesOTCountryBenchmarkType.NONE &&
                    eventRateBenchmarkType !== EventRatesOTBenchmarkType.NONE && (
                        <div className="k-justify-items-left ">
                            <div>
                                <span className="k-pr-4">
                                    <span
                                        style={{
                                            color: Colours.BLACK,
                                            fontSize: 'large',
                                            marginRight: '12px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {eventRateBenchmarkText}: {FormatNumberForChart(benchmarkValue)}
                                </span>
                            </div>
                        </div>
                    )}
                {eventRateThresholdType != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <div className="k-gap-sm">
                            <span className="k-pr-4">
                                <span
                                    style={{
                                        fontWeight: '500',
                                    }}
                                >
                                    {i18next.t('cm:componentsClean.charts.thresholdType')}:{' '}
                                </span>
                                {thresholdText}
                            </span>
                        </div>
                    </div>
                )}
                <div className={styles.infoTextDivider} />

                {upperRed != null && upperAmber != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.upperThresholds')}:{' '}
                        </span>
                        <div>
                            <div className="k-pr-4">
                                <span
                                    style={{
                                        color: Colours.RED,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(upperRed)}
                                <span
                                    style={{
                                        color: Colours.AMBER,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        marginLeft: '66px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(upperAmber)}
                            </div>
                        </div>
                    </div>
                )}
                {lowerRed != null && lowerAmber != null && (
                    <div className="k-justify-items-left k-pb-1">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.lowerThresholds')}:{' '}
                        </span>
                        <div className="k-gap-sm">
                            <div className="k-pr-4">
                                <span
                                    style={{
                                        color: Colours.RED,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(lowerRed)}
                                <span
                                    style={{
                                        color: Colours.AMBER,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        marginLeft: '66px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(lowerAmber)}
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.infoTextDivider} />

                {plotCalculation != null && plotBy != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.plotCalculations')}:{' '}
                        </span>
                        <br />
                        {convertPlotCalculation(plotCalculation, plotBy)}
                    </div>
                )}
                {minSubjects != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.minSubjects')}:{' '}
                        </span>
                        {minSubjects}
                    </div>
                )}
                {minTimeOnStudy != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span style={{ fontWeight: '500' }}>
                            {i18next.t('cm:componentsClean.charts.minTimeOnStudy')}:{' '}
                        </span>

                        <span>
                            {minTimeOnStudy.unit}{' '}
                            {i18next.t(`cm:componentsClean.charts.minTimePeriod.${minTimeOnStudy.period}`)}
                        </span>
                    </div>
                )}

                {/* divider */}
                <div className={styles.infoTextDivider} />
                {/* below min data */}
                <div className={cn('', styles.infoTextRow)}>
                    <div className={cn('', styles.infoTextLabel)}>
                        <div
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-flex',
                                borderRadius: '50%',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.belowMinData')}</div>
                </div>
                {/* no new data */}
                <div className={styles.infoTextRow}>
                    <div className={styles.infoTextLabel}>
                        <div
                            style={{
                                backgroundColor: Colours.RED,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                            }}
                        />
                        <div
                            style={{
                                backgroundColor: Colours.AMBER,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                            }}
                        />
                        <div
                            style={{
                                backgroundColor: Colours.GREEN,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.noNewData')}</div>
                </div>
                {/* no new data & below min data */}
                <div className={styles.infoTextRow}>
                    <div className={styles.infoTextLabel}>
                        <div
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>
                        {i18next.t('cm:componentsClean.charts.noNewData')} &{' '}
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </div>
                </div>
                {/* data lag zone */}
                <div className={styles.infoTextRow}>
                    <div className={cn('', styles.infoTextLabel)}>
                        <div
                            style={{
                                borderLeft: '1px solid #b3b3b3',
                                borderRight: '1px solid #b3b3b3',
                                backgroundColor: styles['rag-colour-grey-25'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.dataLagZone')}</div>
                </div>
            </div>
        </div>
    );
};

const convertColorDirectionThreshold = (color?: ColorDirectionThreshold | null) => {
    switch (color) {
        case ColorDirectionThreshold.UpperRed:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.UpperRed');
        case ColorDirectionThreshold.LowerRed:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.LowerRed');
        case ColorDirectionThreshold.UpperAmber:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.UpperAmber');
        case ColorDirectionThreshold.LowerAmber:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.LowerAmber');
        case ColorDirectionThreshold.Grey:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.Grey');
        case ColorDirectionThreshold.Green:
            return i18next.t('cm:componentsClean.charts.colorDirectionThreshold.Green');
        default:
            return 'None';
    }
};

export const eventRateTooltip =
    (yAxisTitle?: string | null, numeratorName?: string | null, denominatorName?: string | null) => (point: Point) => {
        const plotPoint = point as unknown as ScatterPlotPoint;

        return ReactDOMServer.renderToStaticMarkup(
            <div>
                <b>{i18next.t('cm:componentsClean.charts.timePeriod')}: </b>
                {stringDateWithoutTimeFromUtc(new Date(plotPoint.x!), 'DD-MMM-YYYY')}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.rate')}: </b>
                {FormatNumberForChart(plotPoint.y)} - {yAxisTitle}
                <br />
                <b>{numeratorName}: </b>
                {FormatNumberForChart(plotPoint.numeratorEvents)}
                <br />
                <b>{denominatorName}: </b>
                {FormatNumberForChart(plotPoint.denominatorEvents)}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.status')}: </b>
                {convertColorDirectionThreshold(plotPoint.colorDirectionThreshold)}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.outOfTolerance')}: </b>
                {plotPoint.outOfTolerance != null
                    ? FormatNumberForChart(plotPoint.outOfTolerance)
                    : i18next.t('common:none')}
            </div>,
        );
    };

export const mapEventRatesToScatterPlot = (
    data: EventRatesData,
    studyNumber: string | null | undefined,
): {
    scatterPlotData: ScatterPlotSeries[];
    thresholds: LineThresholdSeries[];
    plotBands: Highcharts.XAxisPlotBandsOptions[];
} => {
    const scatterPlotData: ScatterPlotSeries[] = [];
    const thresholds: LineThresholdSeries[] = [];
    const zones: { value?: number; dashStyle: Highcharts.DashStyleValue }[] = [];
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];

    let seriesName = studyNumber;

    let studyOrCountryData = data.studyPoints;
    if (data.countryData != null && Object.keys(data.countryData).length === 1) {
        [seriesName] = Object.keys(data.countryData);
        studyOrCountryData = data.countryData[Object.keys(data.countryData)[0]].points;
    }

    if (studyOrCountryData == null) {
        throw Error('Event Rate panel result object had no data');
    }

    if (studyOrCountryData) {
        const sortedPoints = studyOrCountryData.sort(
            (a, b) =>
                (parseDateField(a.binLabelDate)?.getTime() || 0) - (parseDateField(b.binLabelDate)?.getTime() || 0),
        );

        let pointsToShade = 0;

        for (let i = 0; i < sortedPoints.length; i++) {
            if (sortedPoints[i].isShaded) pointsToShade++;
        }

        if (pointsToShade > 0)
            plotBands.push({
                color: Colours.GREY,
                from: parseDateField(sortedPoints[sortedPoints.length - pointsToShade].binLabelDate)?.getTime(),
                to: Infinity,
            });

        const plotData: ScatterPlotPoint[] = [];
        studyOrCountryData.forEach((point) => {
            let markerColor: ScoreColor;
            switch (point.colorDirectionThreshold) {
                case ColorDirectionThreshold.UpperRed:
                case ColorDirectionThreshold.LowerRed:
                    markerColor = ScoreColor.Red;
                    break;
                case ColorDirectionThreshold.UpperAmber:
                case ColorDirectionThreshold.LowerAmber:
                    markerColor = ScoreColor.Amber;
                    break;
                case ColorDirectionThreshold.Grey:
                    markerColor = ScoreColor.Grey;
                    break;
                case ColorDirectionThreshold.Green:
                default:
                    markerColor = ScoreColor.Green;
                    break;
            }

            const pointShape = point.minDataFlag ? 'square' : 'circle';

            plotData.push({
                x: parseDateField(point.binLabelDate)?.getTime(),
                y: point.yValue,
                rag: point.ragStatus || ScoreColor.None,
                numeratorEvents: point.numeratorEvents,
                denominatorEvents: point.denominatorValue,
                colorDirectionThreshold: point.colorDirectionThreshold,
                outOfTolerance: point.outOfToleranceValue,
                marker: { fillColor: getScoreColor(markerColor, false), symbol: pointShape },
            });
        });

        scatterPlotData.push({
            name: seriesName || '',
            color: Colours.DARK_BLUE,
            data: plotData,
            marker: 'circle',
            lineWidth: 1,
            zones,
            zoneAxis: 'x',
        });

        const thresholdsData = studyOrCountryData.reduce<{
            upperRed: LineThresholdSeries['data'];
            lowerRed: LineThresholdSeries['data'];
            lowerAmber: LineThresholdSeries['data'];
            upperAmber: LineThresholdSeries['data'];
        }>(
            (prev, point) => {
                const x = parseDateField(point.binLabelDate)?.getTime();

                point.redUpperValue && prev.upperRed.push({ x, y: point.redUpperValue });
                point.redLowerValue && prev.lowerRed.push({ x, y: point.redLowerValue });
                point.amberLowerValue && prev.lowerAmber.push({ x, y: point.amberLowerValue });
                point.amberUpperValue && prev.upperAmber.push({ x, y: point.amberUpperValue });

                return prev;
            },
            {
                upperRed: [],
                lowerRed: [],
                lowerAmber: [],
                upperAmber: [],
            },
        );

        thresholdsData.upperRed.length > 0 &&
            thresholds.push({
                color: Colours.RED,
                data: thresholdsData.upperRed,
                type: 'line',
                style: 'Dash',
            });

        thresholdsData.lowerRed.length > 0 &&
            thresholds.push({
                color: Colours.RED,
                data: thresholdsData.lowerRed,
                type: 'line',
                style: 'Dash',
            });

        thresholdsData.upperAmber.length > 0 &&
            thresholds.push({
                color: Colours.AMBER,
                data: thresholdsData.upperAmber,
                type: 'line',
                style: 'Dash',
            });

        thresholdsData.lowerAmber.length > 0 &&
            thresholds.push({
                color: Colours.AMBER,
                data: thresholdsData.lowerAmber,
                type: 'line',
                style: 'Dash',
            });
    }

    return { scatterPlotData, thresholds, plotBands };
};

const mapPropOTPointsToColouredTrend = (
    points: ProportionsOTPoint[] | null | undefined,
    colours: ScoreColor[],
    studyName: string | null | undefined,
) => {
    const scatterPlotSeries: ScatterPlotSeries[] = [];

    if (points) {
        const plotData = points.map((p) => {
            const x = parseDateField(p.binLabelDate)?.getTime();

            const markerSymbol = p.noNewData === true ? 'square' : 'circle';

            return {
                x,
                y: p.yValue,
                rag: p.noNewData ? ScoreColor.Grey : p.ragStatus || ScoreColor.None,
                marker: {
                    fillColor: p.minDataFlag
                        ? getScoreColor(colours.filter((c) => c === ScoreColor.Grey)[0], false)
                        : getScoreColor(colours.filter((c) => c === p.ragStatus)[0], false),
                    symbol: markerSymbol,
                },
                numeratorEvents: p.numeratorEvents,
                denominatorEvents: p.denominatorValue,
                colorDirectionThreshold: p.colorDirectionThreshold,
                outOfTolerance: p.outOfToleranceValue,
            };
        });

        scatterPlotSeries.push({
            name: studyName ?? '',
            data: plotData,
            color: Colours.DARK_BLUE,
            marker: 'circle',
            lineWidth: 1,
            zoneAxis: 'x',
        });
    }

    return scatterPlotSeries;
};
export const mapPropOTToScatter = (
    points: ProportionsOTPoint[] | null | undefined,
    benchmarkValue?: number | null,
    studyName?: string | null,
): {
    scatterPlotData: ScatterPlotSeries[];
    thresholds: LineThresholdSeries[];
    plotBands: Highcharts.XAxisPlotBandsOptions[];
} => {
    const scatterPlotData: ScatterPlotSeries[] = mapPropOTPointsToColouredTrend(points, RAGGrey, studyName);
    const thresholds: LineThresholdSeries[] = [];
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];

    // shaded points
    if (points) {
        const shadedPoints = points!.filter((p) => p.isShaded).length;

        plotBands.push({
            color: Colours.GREY,
            from: parseDateField(points[points.length - shadedPoints]?.binLabelDate)?.getTime(),
            to: parseDateField(points[points.length - 1]?.binLabelDate)?.getTime(),
        });
    }

    const thresholdsData = points?.reduce<{
        benchmark: LineThresholdSeries['data'];
        upperRed: LineThresholdSeries['data'];
        lowerRed: LineThresholdSeries['data'];
        lowerAmber: LineThresholdSeries['data'];
        upperAmber: LineThresholdSeries['data'];
    }>(
        (prev, point) => {
            const x = parseDateField(point.binLabelDate)?.getTime();

            if (benchmarkValue) prev.benchmark.push({ x, y: benchmarkValue });
            prev.upperRed.push({ x, y: point.redUpperValue ?? undefined });
            prev.upperAmber.push({ x, y: point.amberUpperValue ?? undefined });
            prev.lowerRed.push({ x, y: point.redLowerValue ?? undefined });
            prev.lowerAmber.push({ x, y: point.amberLowerValue ?? undefined });

            return prev;
        },
        {
            benchmark: [],
            upperRed: [],
            lowerRed: [],
            lowerAmber: [],
            upperAmber: [],
        },
    );

    thresholds.push({
        color: Colours.BLACK,
        data: thresholdsData?.benchmark ?? [],
        type: 'line',
        style: 'Dash',
    });
    thresholds.push({
        color: Colours.RED,
        data: thresholdsData?.upperRed ?? [],
        type: 'line',
        style: 'Dash',
    });
    thresholds.push({
        color: Colours.AMBER,
        data: thresholdsData?.upperAmber ?? [],
        type: 'line',
        style: 'Dash',
    });
    thresholds.push({
        color: Colours.AMBER,
        data: thresholdsData?.lowerAmber ?? [],
        type: 'line',
        style: 'Dash',
    });
    thresholds.push({
        color: Colours.RED,
        data: thresholdsData?.lowerRed ?? [],
        type: 'line',
        style: 'Dash',
    });

    return { scatterPlotData, thresholds, plotBands };
};
export const propOTTooltip =
    (yAxisTitle?: string | null, numeratorName?: string | null, denominatorName?: string | null) => (point: Point) => {
        const plotPoint = point as unknown as ScatterPlotPoint;

        return ReactDOMServer.renderToStaticMarkup(
            <div>
                <b>{i18next.t('cm:componentsClean.charts.timePeriod')}: </b>
                {stringDateWithoutTimeFromUtc(new Date(plotPoint.x!), 'DD-MMM-YYYY')}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.rate')}: </b>
                {FormatNumberForChart(plotPoint.y)} {yAxisTitle}
                <br />
                <b>{numeratorName}</b>: {plotPoint.numeratorEvents}
                <br />
                <b>{denominatorName}</b>: {plotPoint.denominatorEvents}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.status')}: </b>
                {convertColorDirectionThreshold(plotPoint.colorDirectionThreshold)}
                <br />
                <b>{i18next.t('cm:componentsClean.charts.outOfTolerance')}: </b>
                {plotPoint.outOfTolerance != null
                    ? bankersRound(plotPoint.outOfTolerance, 2)
                    : i18next.t('common:none')}
            </div>,
        );
    };
export const getPropOTSubtitle = (
    benchmarkType?: ProportionsOTBenchmarkType | ProportionsOTCountryBenchmarkType | null,
    benchmarkValue?: number | null,
    thresholdType?: ProportionsOTThresholdType | null,
    plotCalculation?: ModelPlotCalculation | null,
    plotBy?: ModelPlotBy | null,
    upperAmber?: number | null,
    lowerAmber?: number | null,
    upperRed?: number | null,
    lowerRed?: number | null,
    minSubjects?: number | null,
) => {
    const benchmarkText = i18next.t(`cm:componentsClean.charts.benchmarkTypeValue.${benchmarkType}`);
    const thresholdText = i18next.t(`cm:componentsClean.charts.thresholdTypeValue.${thresholdType}`);

    return ReactDOMServer.renderToStaticMarkup(
        <div style={{ textAlign: 'center' }}>
            <div>
                {/* benchmark and threshold */}
                <div className="k-justify-items-left k-pb-2">
                    {benchmarkValue != null && (
                        <span>
                            <span>
                                <span style={{ paddingRight: '16px' }}>
                                    <span
                                        style={{
                                            color: Colours.BLACK,
                                            fontSize: 'large',
                                            marginRight: '12px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    Benchmark {benchmarkText}: {FormatNumberForChart(benchmarkValue)}
                                </span>
                            </span>
                        </span>
                    )}
                    {thresholdText != null && (
                        <span>
                            <span className="k-gap-sm">
                                <span>
                                    <span
                                        style={{
                                            fontWeight: '500',
                                        }}
                                    >
                                        {i18next.t('cm:componentsClean.charts.thresholdType')}:{' '}
                                    </span>
                                    Threshold {thresholdText}
                                </span>
                            </span>
                        </span>
                    )}
                </div>
                {/* upper / lower thresholds */}
                <div className="k-justify-items-left k-pb-2">
                    {/* upper thresholds */}
                    {upperRed != null && upperAmber != null && (
                        <span style={{ paddingRight: '8px' }}>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.upperThresholds')}:{' '}
                            </span>
                            <span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.RED,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(upperRed)}
                                </span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.AMBER,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(upperAmber)}
                                </span>
                            </span>
                        </span>
                    )}
                    {/* lower thresholds */}
                    {lowerRed != null && lowerAmber != null && (
                        <span>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.lowerThresholds')}:{' '}
                            </span>
                            <span className="k-gap-sm">
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.RED,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(lowerRed)}
                                </span>
                                <span style={{ paddingRight: '8px' }}>
                                    <span
                                        style={{
                                            color: Colours.AMBER,
                                            fontSize: 'large',
                                            marginRight: '6px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        - - -
                                    </span>
                                    {FormatNumberForChart(lowerAmber)}
                                </span>
                            </span>
                        </span>
                    )}
                </div>
                {/* plot by / min data */}
                <div className="k-justify-items-left k-pb-2">
                    {plotCalculation != null && plotBy != null && (
                        <span style={{ paddingRight: '8px' }}>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.plotCalc')}:{' '}
                            </span>
                            {plotCalculation === ModelPlotCalculation.CUMULATIVE
                                ? i18next.t(`cm:componentsClean.charts.plotCalculation.${plotCalculation}`)
                                : convertPlotCalculation(plotCalculation, plotBy)}
                        </span>
                    )}
                    {minSubjects != null && (
                        <span className="k-justify-items-left k-pb-2">
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.minData')}:{' '}
                            </span>
                            {minSubjects}
                        </span>
                    )}
                </div>
                {/* min data; no new data; no new data/below min data; data lag zone */}
                <div className="k-justify-items-left">
                    {/* below min data */}
                    <span style={{ paddingRight: '8px' }}>
                        <span
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-flex',
                                borderRadius: '50%',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </span>
                    {/* no new data */}
                    <span style={{ paddingInline: '8px' }}>
                        <span
                            style={{
                                backgroundColor: Colours.GREEN,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        <span
                            style={{
                                backgroundColor: Colours.AMBER,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        <span
                            style={{
                                backgroundColor: Colours.RED,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />{' '}
                        {i18next.t('cm:componentsClean.charts.noNewData')}
                    </span>
                    {/* no new data & below min data */}
                    <span style={{ paddingInline: '8px' }}>
                        <span
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />{' '}
                        {i18next.t('cm:componentsClean.charts.noNewData')} &{' '}
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </span>
                    {/* data lag zone */}
                    <span style={{ paddingLeft: '8px' }}>
                        <span
                            style={{
                                borderLeft: '1px solid #b3b3b3',
                                borderRight: '1px solid #b3b3b3',
                                backgroundColor: styles['rag-colour-grey-25'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '4px',
                                printColorAdjust: 'exact',
                            }}
                        />
                        {i18next.t('cm:componentsClean.charts.dataLagZone')}
                    </span>
                </div>
            </div>
        </div>,
    );
};
export const getPropOTInfoText = (
    benchmarkType?: ProportionsOTBenchmarkType | ProportionsOTCountryBenchmarkType | null,
    benchmarkValue?: number | null | undefined,
    thresholdType?: ProportionsOTThresholdType | null,
    plotCalculation?: ModelPlotCalculation | null,
    plotBy?: ModelPlotBy | null,
    upperAmber?: number | null,
    lowerAmber?: number | null,
    upperRed?: number | null,
    lowerRed?: number | null,
    minData?: number | null,
) => {
    // method called when hovering over the info button
    const benchmarkText = i18next.t(`cm:componentsClean.charts.benchmarkTypeValue.${benchmarkType}`);
    const thresholdText = i18next.t(`cm:componentsClean.charts.thresholdTypeValue.${thresholdType}`);

    return (
        <div style={{ paddingLeft: 5, width: '270px' }}>
            <div>
                {benchmarkValue != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <div>
                            <span className="k-pr-4">
                                <span
                                    style={{
                                        color: Colours.BLACK,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {benchmarkText}: {FormatNumberForChart(benchmarkValue)}
                            </span>
                        </div>
                    </div>
                )}
                {thresholdText != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <div className="k-gap-sm">
                            <span className="k-pr-4">
                                <span
                                    style={{
                                        fontWeight: '500',
                                    }}
                                >
                                    {i18next.t('cm:componentsClean.charts.thresholdType')}:{' '}
                                </span>
                                <br />
                                {thresholdText}
                            </span>
                        </div>
                    </div>
                )}
                {/* divider */}
                <div className={styles.infoTextDivider} />
                {/* upper thresholds */}
                {upperRed != null && upperAmber != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.upperThresholds')}:{' '}
                        </span>
                        <div>
                            <span className="k-pr-12">
                                <span
                                    style={{
                                        color: Colours.RED,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(upperRed)}
                            </span>
                            <span>
                                <span
                                    style={{
                                        color: Colours.AMBER,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(upperAmber)}
                            </span>
                        </div>
                    </div>
                )}
                {/* lower thresholds */}
                {lowerRed != null && lowerAmber != null && (
                    <div className="k-justify-items-left k-pb-2">
                        <span
                            style={{
                                fontWeight: '500',
                            }}
                        >
                            {i18next.t('cm:componentsClean.charts.lowerThresholds')}:{' '}
                        </span>
                        <div className="k-gap-sm">
                            <span className="k-pr-12">
                                <span
                                    style={{
                                        color: Colours.RED,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(lowerRed)}
                            </span>
                            <span>
                                <span
                                    style={{
                                        color: Colours.AMBER,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(lowerAmber)}
                            </span>
                        </div>
                    </div>
                )}
                {/* divider */}
                <div className={styles.infoTextDivider} />
                {/* plot calculation */}
                {plotCalculation != null && plotBy != null && (
                    <div className={styles.infoTextRowLongLabel}>
                        <div className={cn('k-justify-items-left k-pb-2')}>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.plotCalc')}:{' '}
                            </span>
                        </div>
                        {/* plot calc: cumulative - don't show plot by */}
                        {plotCalculation === ModelPlotCalculation.CUMULATIVE && (
                            <div className={styles.infoTextValue}>
                                {i18next.t(`cm:componentsClean.charts.plotCalculation.${plotCalculation}`)}
                            </div>
                        )}
                        {/* plot calc: time period - show plot by (e.g. week, month */}
                        {plotCalculation !== ModelPlotCalculation.CUMULATIVE && (
                            <div className={styles.infoTextValue}>
                                {convertPlotCalculation(plotCalculation, plotBy)}
                            </div>
                        )}
                    </div>
                )}
                {/* min data */}
                {minData != null && (
                    <div className={styles.infoTextRowLongLabel}>
                        <div>
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {i18next.t('cm:componentsClean.charts.minData')}:{' '}
                            </span>
                        </div>
                        <div className={styles.infoTextValue}> {minData}</div>
                    </div>
                )}
                {/* divider */}
                <div className={styles.infoTextDivider} />
                {/* below min data */}
                <div className={cn('', styles.infoTextRow)}>
                    <div className={cn('', styles.infoTextLabel)}>
                        <div
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-flex',
                                borderRadius: '50%',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.belowMinData')}</div>
                </div>
                {/* no new data */}
                <div className={styles.infoTextRow}>
                    <div className={styles.infoTextLabel}>
                        <div
                            style={{
                                backgroundColor: Colours.GREEN,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                            }}
                        />
                        <div
                            style={{
                                backgroundColor: Colours.AMBER,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                                marginRight: '6px',
                            }}
                        />
                        <div
                            style={{
                                backgroundColor: Colours.RED,
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.noNewData')}</div>
                </div>
                {/* no new data & below min data */}
                <div className={styles.infoTextRow}>
                    <div className={styles.infoTextLabel}>
                        <div
                            style={{
                                backgroundColor: styles['rag-colour-grey'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>
                        {i18next.t('cm:componentsClean.charts.noNewData')} &{' '}
                        {i18next.t('cm:componentsClean.charts.belowMinData')}
                    </div>
                </div>
                {/* data lag zone */}
                <div className={styles.infoTextRow}>
                    <div className={cn('', styles.infoTextLabel)}>
                        <div
                            style={{
                                borderLeft: '1px solid #b3b3b3',
                                borderRight: '1px solid #b3b3b3',
                                backgroundColor: styles['rag-colour-grey-25'],
                                width: '12px',
                                height: '12px',
                                display: 'inline-block',
                            }}
                        />
                    </div>
                    <div className={styles.infoTextValue}>{i18next.t('cm:componentsClean.charts.dataLagZone')}</div>
                </div>
            </div>
        </div>
    );
};
